import {HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest} from '@angular/common/http';
import {DOCUMENT, Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {
    AuthConfigService,
    AuthHttpHeaderService,
    AuthInterceptor,
    BaseSiteService,
    GlobalMessageService,
    GlobalMessageType,
    InterceptorUtil,
    RoutingService,
    StateWithClientAuth,
} from '@spartacus/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';

import {openCloseSpinner} from '../../cms-components/shared/utils/spinner/aimo-spinner-utils';
import {AimoAuthRedirectService} from '../../service/auth/aimo-auth-redirect.service';
import {LoginFail} from '../../service/auth/login-logout-fail.action';
import {AimoCdcLoginFailed} from "../../service/user/aimo-user.action";
import {AimoBaseSite} from "../../model/misc.model";
import {AimoAuthService} from "../../service/auth/aimo-auth.service";

export const AIMO_PROPAGATE_HTTP_ERROR = 'aimo-propagate-http-error';


@Injectable({providedIn: 'root'})
export class ErrorsHttpInterceptor extends AuthInterceptor {
    cdcMode: boolean = false;

    constructor(
        protected authHttpHeaderService: AuthHttpHeaderService,
        protected authConfigService: AuthConfigService,
        public router: Router,
        protected auth: AimoAuthService,
        protected routingService: RoutingService,
        protected authRedirectService: AimoAuthRedirectService,
        protected store: Store<StateWithClientAuth>,
        protected messageService: GlobalMessageService,
        protected baseSiteService: BaseSiteService,
        @Inject(DOCUMENT) private document: Document
    ) {
        super(authHttpHeaderService, authConfigService);
        this.baseSiteService.get().subscribe((baseSite: AimoBaseSite) => {
            this.cdcMode = baseSite?.cdcApikey !== undefined;
        });
    }

    intercept(httpRequest: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const propagateError = !!InterceptorUtil.getInterceptorParam(AIMO_PROPAGATE_HTTP_ERROR, httpRequest.headers);
        httpRequest = InterceptorUtil.removeHeader(AIMO_PROPAGATE_HTTP_ERROR, httpRequest);

        this.authHttpHeaderService.shouldCatchError(httpRequest);
        const shouldAddAuthorizationHeader = this.authHttpHeaderService.shouldAddAuthorizationHeader(httpRequest);

        const token$ = shouldAddAuthorizationHeader
            ? // emits sync, unless there is refresh or logout in progress, in which case it emits async
            this.authHttpHeaderService.getStableToken().pipe(take(1))
            : of(undefined);
        const requestAndToken$ = token$.pipe(
            map((token) => ({
                token,
                request: this.authHttpHeaderService.alterRequest(httpRequest, token),
            })),
        );

        return requestAndToken$.pipe(
            switchMap(({request, token}) =>
                next.handle(request).pipe(
                    catchError((errResponse: unknown) => {
                        if (errResponse instanceof HttpErrorResponse) {
                            switch (errResponse.status) {
                                case 500:
                                    if (errResponse.url?.includes('/revoke')) { // double revokes throw error sometimes
                                        break;
                                    }
                                    return throwError(errResponse);
                                case 403: // cannot login to current store
                                    this.auth
                                        .coreLogout()
                                        .then(() => {
                                            openCloseSpinner(this.document, false);
                                            if (this.cdcMode) {
                                                this.store.dispatch(new AimoCdcLoginFailed());
                                                //this.routingService.go('/login');
                                            } else {
                                                this.authRedirectService.setRedirectUrl('/login');
                                                this.store.dispatch(new LoginFail());
                                                this.routingService.go('/login');
                                            }
                                        })
                                        // eslint-disable-next-line no-console
                                        .catch((ex) => console.error(ex));
                                    break;
                                case 400:
                                    openCloseSpinner(this.document, false);
                                    const cdcLoginError = errResponse.url?.includes('/auth/cdc');
                                    if (cdcLoginError && !this.cdcMode) {
                                        this.messageService.add({key: 'httpHandlers.badRequest.bad_credentials'}, GlobalMessageType.MSG_TYPE_ERROR, 999000);
                                    }
                                    if (cdcLoginError) {
                                        this.store.dispatch(new AimoCdcLoginFailed());
                                    }
                                    if (propagateError) {
                                        return throwError(errResponse);
                                    }
                                    break;
                                case 401:
                                    this.auth
                                        .coreLogout()
                                        .then(() => {
                                            const url = document.location.href;
                                            const ssoParam = url.includes('sso=true') ? '?sso=true' : '';
                                            this.messageService.add({key: 'httpHandlers.sessionExpired'}, GlobalMessageType.MSG_TYPE_ERROR, 999000);
                                            this.routingService.go(['/login' + ssoParam]);
                                        });
                                    break;
                                default:
                                    return throwError(errResponse);
                            }
                        }
                    }),
                ),
            ),
        );
    }
}
